<script setup lang="ts">
import { isAppLocale } from '~/constants/appLocale'
import type { CVData } from '~/types'
import { cvFindings, cvReviewScore, findingPoints, halfText } from '~/utils/cvReview'

/**
 * "Your CV scores 41 — the fixes take it to 95", over the result.
 *
 * Where the day-0 e-mail's button lands (?focus=review): the same review as
 * the e-mail (utils/cvReview.ts), drawn as a score ring, the three findings
 * the e-mail named in full, and the rest by name only, locked. A CV with no
 * more fixes than the free ones gets them all locked, so the pack still has
 * something to unlock. The button is the Job Search Pack, whose AI rewrites
 * the texts in the editor; editing by hand for free stays one link under it.
 * Like the tailoring card (LandingTailorDemo), it shows what is wrong and what fixing it is worth
 * before it asks for money.
 *
 * The editor shows it too, over the CV (pages/cv/[id].vue, ?review=1), for
 * the same e-mail sent to an account.
 *
 * A CV the rules find nothing on gets no locked list: it is sent on to the
 * tailoring instead, which is the next thing that moves it.
 */
const props = withDefaults(defineProps<{
  cvData: CVData
  /** The pack can be bought: the findings past the first three are locked. */
  limited?: boolean
  /** A checkout is opening; disables the button. */
  isBuying?: boolean
  /** The offers are still loading: the buttons wait. */
  ready?: boolean
  /** In a dialog with its own close button in the corner: no icon there. */
  dialog?: boolean
}>(), {
  limited: true,
  isBuying: false,
  ready: true,
  dialog: false,
})

const emit = defineEmits<{
  /** Buy the pack; the offers open its checkout. */
  buy: []
  /** Save the CV and open the editor (an account first, when signed out). */
  edit: []
  /** Nothing to fix: tailor it to a posting instead. */
  tailor: []
}>()

const { t, locale } = useI18n()
/** Less what was paid for this CV before, as the checkout charges it (usePackCredit). */
const { packPrice: jobPackPrice, load: loadPackCredit } = usePackCredit()
// In the editor's dialog nothing else asks: the account's own purchases, by its address.
onMounted(() => {
  if (props.dialog) loadPackCredit()
})

/**
 * Without a plan: the first fix whole, the second in half, like the day-0 e-mail.
 * When that would be all of them, none is free: nothing would be left to buy.
 */
const FREE_FINDINGS = 2

const findings = computed(() => cvFindings(props.cvData, isAppLocale(locale.value) ? locale.value : 'en'))
const score = computed(() => cvReviewScore(findings.value))
const gain = computed(() => score.value.after - score.value.before)
/** What the pack does about them, one line each. */
const packLines = ['ai', 'tailor', 'clean'] as const
/** Every fix is locked: the free ones would have given them all away. */
const allLocked = computed(() => props.limited && findings.value.length <= FREE_FINDINGS)
const free = computed(() => (!props.limited ? findings.value : allLocked.value ? [] : findings.value.slice(0, FREE_FINDINGS)))
const locked = computed(() => (!props.limited ? [] : allLocked.value ? findings.value : findings.value.slice(FREE_FINDINGS)))
/** The second free fix shows only its first half. */
const isHalf = (index: number) => props.limited && index === FREE_FINDINGS - 1

/* The ring: what the CV has now, and the arc the fixes add after it. */
const RADIUS = 52
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const arc = (from: number, length: number) => ({
  'stroke-dasharray': `${(length / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`,
  'stroke-dashoffset': `${-(from / 100) * CIRCUMFERENCE}`,
})
/** Starts empty and fills on mount, so the gain is seen being added. */
const drawn = ref(false)
onMounted(() => requestAnimationFrame(() => (drawn.value = true)))

const scoreClass = (value: number) => (value >= 75 ? 'text-accent-strong dark:text-accent-soft' : value >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400')
</script>

<template>
  <div class="card !p-5 md:!p-7">
    <div class="mb-5 flex items-start justify-between gap-4">
      <div>
        <div class="font-title text-xl font-bold text-ink md:text-2xl dark:text-white">{{ t('landing.builder.review.title') }}</div>
        <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.review.subtitle') }}</p>
      </div>
      <i v-if="!dialog" class="bi bi-clipboard2-pulse shrink-0 text-4xl leading-none text-lilac" aria-hidden="true" />
    </div>

    <div class="grid gap-6 md:grid-cols-[auto_1fr] md:gap-8">
      <!-- The score: a ring, now and with the fixes -->
      <div class="flex items-center gap-5 md:flex-col md:items-center md:gap-3">
        <div class="relative size-32 shrink-0 md:size-40">
          <svg viewBox="0 0 120 120" class="size-full -rotate-90" aria-hidden="true">
            <circle cx="60" cy="60" :r="RADIUS" fill="none" stroke-width="11" class="stroke-line dark:stroke-gray-700" />
            <circle
              cx="60"
              cy="60"
              :r="RADIUS"
              fill="none"
              stroke-width="11"
              stroke-linecap="butt"
              class="stroke-accent/30 transition-[stroke-dasharray] delay-500 duration-1000 ease-out"
              :style="arc(score.before, drawn ? gain : 0)"
            />
            <circle
              cx="60"
              cy="60"
              :r="RADIUS"
              fill="none"
              stroke-width="11"
              stroke-linecap="butt"
              class="transition-[stroke-dasharray] duration-700 ease-out"
              :class="score.before >= 75 ? 'stroke-accent' : score.before >= 50 ? 'stroke-amber-500' : 'stroke-red-500'"
              :style="arc(0, drawn ? score.before : 0)"
            />
          </svg>
          <div class="absolute inset-0 flex flex-col items-center justify-center">
            <span class="font-title !text-4xl font-bold !leading-none tabular-nums md:!text-5xl" :class="scoreClass(score.before)">{{ score.before }}</span>
            <span class="mt-1 !text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('landing.builder.review.outOf') }}</span>
          </div>
        </div>

        <div class="md:text-center" aria-live="polite">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('landing.builder.review.after') }}</div>
          <div class="flex items-baseline gap-2 font-title font-bold tabular-nums md:justify-center">
            <span class="!text-3xl text-accent-strong dark:text-accent-soft">{{ score.after }}</span>
            <span v-if="gain > 0" class="rounded-full bg-accent-strong px-2.5 py-0.5 !text-sm !text-white">+{{ gain }}</span>
          </div>
          <p v-if="findings.length" class="mt-1 max-w-[14rem] !text-sm text-body dark:text-gray-300">
            {{ t('landing.builder.review.found', { n: findings.length }, findings.length) }}
          </p>
        </div>
      </div>

      <!-- What to fix -->
      <div class="min-w-0">
        <!-- Nothing the rules can find: the next step is the posting -->
        <template v-if="!findings.length">
          <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('landing.builder.review.cleanTitle') }}</div>
          <p class="mb-4 mt-1 text-base">{{ t('landing.builder.review.cleanText') }}</p>
          <SofaxBtn variant="dark" block :disabled="!ready" :label="t('landing.builder.review.cleanCta')" @click="emit('tailor')" />
        </template>

        <template v-else>
          <div v-if="free.length" class="mb-2 flex items-center gap-2 font-title text-base font-semibold text-ink dark:text-white">
            {{ limited ? t('landing.builder.review.freeTitle') : t('landing.builder.review.allTitle') }}
            <span v-if="limited" class="rounded-full bg-accent/15 px-2 py-0.5 !text-xs font-bold uppercase tracking-wide text-accent-strong dark:text-accent-soft">{{ t('landing.builder.review.freeBadge') }}</span>
          </div>
          <ul v-if="free.length" class="mb-5 space-y-2">
            <li v-for="(finding, index) in free" :key="finding.id" class="rounded-[0.625rem] border border-line p-3 dark:border-gray-700">
              <div class="flex items-start gap-2">
                <i class="bi bi-lightbulb-fill mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-3">
                    <div class="font-title text-sm font-semibold text-ink dark:text-white">{{ finding.title }}</div>
                    <span class="shrink-0 !text-xs font-bold tabular-nums text-accent-strong dark:text-accent-soft">{{ t('landing.builder.review.points', { n: findingPoints(finding.id) }) }}</span>
                  </div>
                  <p v-if="!isHalf(index)" class="mt-0.5 !text-sm text-body dark:text-gray-300">{{ finding.text }}</p>
                  <!-- Half of it: the text fades out, the rest is in the pack -->
                  <template v-else>
                    <p class="relative mt-0.5 !text-sm text-body dark:text-gray-300">
                      {{ halfText(finding.text) }}
                      <span class="pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-white dark:from-gray-900" aria-hidden="true" />
                    </p>
                    <div class="mt-1.5 flex items-center gap-1.5 !text-xs font-semibold text-muted dark:text-gray-400">
                      <i class="bi bi-lock-fill" aria-hidden="true" />
                      {{ t('landing.builder.review.halfLocked') }}
                    </div>
                  </template>
                </div>
              </div>
            </li>
          </ul>

          <!-- The rest by name, its text locked behind the pack -->
          <div v-if="locked.length" class="mb-5">
            <div class="mb-2 font-title text-base font-semibold text-ink dark:text-white">
              {{ t(allLocked ? 'landing.builder.review.lockedAllTitle' : 'landing.builder.review.lockedTitle', { n: locked.length }, locked.length) }}
            </div>
            <ul class="space-y-2">
              <li v-for="finding in locked" :key="finding.id" class="rounded-[0.625rem] border border-line bg-surface/60 p-3 dark:border-gray-700 dark:bg-gray-900/60">
                <div class="flex items-start gap-2">
                  <i class="bi bi-lock-fill mt-0.5 shrink-0 text-muted" aria-hidden="true" />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-baseline justify-between gap-3">
                      <div class="font-title text-sm font-semibold text-ink dark:text-white">{{ finding.title }}</div>
                      <span class="shrink-0 !text-xs font-bold tabular-nums text-accent-strong dark:text-accent-soft">{{ t('landing.builder.review.points', { n: findingPoints(finding.id) }) }}</span>
                    </div>
                    <div class="mt-2 space-y-1.5" aria-hidden="true">
                      <div class="h-2 w-11/12 rounded-full bg-line blur-[1.5px] dark:bg-gray-700" />
                      <div class="h-2 w-3/4 rounded-full bg-line blur-[1.5px] dark:bg-gray-700" />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <!-- The pack: the fixes done for them, and everything else it brings -->
          <div v-if="limited" class="rounded-[0.625rem] border-2 border-ink p-4 dark:border-white">
            <div class="flex items-start gap-3">
              <i class="bi bi-stars shrink-0 text-2xl leading-none text-accent" aria-hidden="true" />
              <div class="min-w-0">
                <div class="font-title text-base font-bold text-ink dark:text-white">
                  {{ t('landing.builder.review.packTitle', { after: score.after }) }}
                </div>
                <ul class="mt-2 space-y-1">
                  <li v-for="key in packLines" :key="key" class="flex items-start gap-2 !text-sm text-body dark:text-gray-300">
                    <i class="bi bi-check-lg mt-0.5 shrink-0 text-accent" aria-hidden="true" />
                    {{ t(`landing.builder.review.pack.${key}`) }}
                  </li>
                </ul>
              </div>
            </div>
            <SofaxBtn
              variant="accent"
              block
              class="mt-4"
              :disabled="!ready || isBuying"
              :label="isBuying ? t('common.loading') : t('landing.builder.review.packCta', { price: jobPackPrice })"
              @click="emit('buy')"
            />
            <p class="mt-2 text-center !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.pack.noRenew') }}</p>
          </div>

          <p class="mt-3 text-center">
            <button type="button" class="link !text-sm disabled:opacity-50" :disabled="!ready" @click="emit('edit')">
              {{ limited ? t('landing.builder.review.diy') : t('landing.builder.review.editCta') }}
            </button>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
